/**
 * Copyright (c) 2018-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 * @author Gianluca Tomasello <giagitom@gmail.com>
 */
import { GridLookup3D, Result } from '../../../../mol-math/geometry';
import { Vec3 } from '../../../../mol-math/linear-algebra';
import { OrderedSet } from '../../../../mol-data/int';
import { StructureElement } from '../element';
import { FibonacciHeap } from '../../../../mol-util/fibonacci-heap';
export var StructureResult;
(function (StructureResult) {
    function add(result, unit, index, distSq) {
        result.indices[result.count] = index;
        result.units[result.count] = unit;
        result.squaredDistances[result.count] = distSq;
        result.count++;
    }
    StructureResult.add = add;
    function create() {
        return { count: 0, indices: [], units: [], squaredDistances: [] };
    }
    StructureResult.create = create;
    function copy(out, result) {
        for (var i = 0; i < result.count; ++i) {
            out.indices[i] = result.indices[i];
            out.units[i] = result.units[i];
            out.squaredDistances[i] = result.squaredDistances[i];
        }
        out.count = result.count;
        return out;
    }
    StructureResult.copy = copy;
})(StructureResult || (StructureResult = {}));
export function StructureLookup3DResultContext() {
    return { result: StructureResult.create(), closeUnitsResult: Result.create(), unitGroupResult: Result.create() };
}
var StructureLookup3D = /** @class */ (function () {
    function StructureLookup3D(structure) {
        this.structure = structure;
        this.pivot = Vec3();
        this.heap = new FibonacciHeap();
        this.findContext = StructureLookup3DResultContext();
        var units = structure.units, boundary = structure.boundary;
        var unitCount = units.length;
        var xs = new Float32Array(unitCount);
        var ys = new Float32Array(unitCount);
        var zs = new Float32Array(unitCount);
        var radius = new Float32Array(unitCount);
        var center = Vec3();
        for (var i = 0; i < unitCount; i++) {
            var unit = units[i];
            var s = unit.boundary.sphere;
            Vec3.transformMat4(center, s.center, unit.conformation.operator.matrix);
            xs[i] = center[0];
            ys[i] = center[1];
            zs[i] = center[2];
            radius[i] = s.radius;
        }
        var position = { x: xs, y: ys, z: zs, radius: radius, indices: OrderedSet.ofBounds(0, unitCount) };
        this.unitLookup = GridLookup3D(position, boundary);
    }
    StructureLookup3D.prototype.findUnitIndices = function (x, y, z, radius) {
        return this.unitLookup.find(x, y, z, radius);
    };
    StructureLookup3D.prototype.find = function (x, y, z, radius, ctx) {
        return this._find(x, y, z, radius, ctx !== null && ctx !== void 0 ? ctx : this.findContext);
    };
    StructureLookup3D.prototype._find = function (x, y, z, radius, ctx) {
        Result.reset(ctx.result);
        var units = this.structure.units;
        var closeUnits = this.unitLookup.find(x, y, z, radius, ctx.closeUnitsResult);
        if (closeUnits.count === 0)
            return ctx.result;
        for (var t = 0, _t = closeUnits.count; t < _t; t++) {
            var unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            var unitLookup = unit.lookup3d;
            var groupResult = unitLookup.find(this.pivot[0], this.pivot[1], this.pivot[2], radius, ctx.unitGroupResult);
            for (var j = 0, _j = groupResult.count; j < _j; j++) {
                StructureResult.add(ctx.result, unit, groupResult.indices[j], groupResult.squaredDistances[j]);
            }
        }
        return ctx.result;
    };
    StructureLookup3D.prototype.nearest = function (x, y, z, k, ctx) {
        if (k === void 0) { k = 1; }
        return this._nearest(x, y, z, k, ctx !== null && ctx !== void 0 ? ctx : this.findContext);
    };
    StructureLookup3D.prototype._nearest = function (x, y, z, k, ctx) {
        var result = ctx.result, heap = this.heap;
        Result.reset(result);
        heap.clear();
        var units = this.structure.units;
        var elementsCount = 0;
        var closeUnits = this.unitLookup.nearest(x, y, z, units.length, function (uid) { return (elementsCount += units[uid].elements.length) >= k; }, ctx.closeUnitsResult); // sort units based on distance to the point
        if (closeUnits.count === 0)
            return result;
        var totalCount = 0, maxDistResult = -Number.MAX_VALUE;
        for (var t = 0, _t = closeUnits.count; t < _t; t++) {
            var unitSqDist = closeUnits.squaredDistances[t];
            if (totalCount >= k && maxDistResult < unitSqDist)
                break;
            Vec3.set(this.pivot, x, y, z);
            var unit = units[closeUnits.indices[t]];
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            var unitLookup = unit.lookup3d;
            var groupResult = unitLookup.nearest(this.pivot[0], this.pivot[1], this.pivot[2], k, void 0, ctx.unitGroupResult);
            if (groupResult.count === 0)
                continue;
            totalCount += groupResult.count;
            maxDistResult = Math.max(maxDistResult, groupResult.squaredDistances[groupResult.count - 1]);
            for (var j = 0, _j = groupResult.count; j < _j; j++) {
                heap.insert(groupResult.squaredDistances[j], { index: groupResult.indices[j], unit: unit });
            }
        }
        if (k === 1) {
            var node = heap.findMinimum();
            if (node) {
                var squaredDistance = node.key;
                var _a = node.value, unit = _a.unit, index = _a.index;
                StructureResult.add(result, unit, index, squaredDistance);
            }
        }
        else {
            while (!heap.isEmpty() && result.count < k) {
                var node = heap.extractMinimum();
                var squaredDistance = node.key;
                var _b = node.value, unit = _b.unit, index = _b.index;
                StructureResult.add(result, unit, index, squaredDistance);
            }
        }
        return result;
    };
    StructureLookup3D.prototype.findIntoBuilder = function (x, y, z, radius, builder) {
        var units = this.structure.units;
        var closeUnits = this.unitLookup.find(x, y, z, radius);
        if (closeUnits.count === 0)
            return;
        for (var t = 0, _t = closeUnits.count; t < _t; t++) {
            var unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            var unitLookup = unit.lookup3d;
            var groupResult = unitLookup.find(this.pivot[0], this.pivot[1], this.pivot[2], radius);
            if (groupResult.count === 0)
                continue;
            var elements = unit.elements;
            builder.beginUnit(unit.id);
            for (var j = 0, _j = groupResult.count; j < _j; j++) {
                builder.addElement(elements[groupResult.indices[j]]);
            }
            builder.commitUnit();
        }
    };
    StructureLookup3D.prototype.findIntoBuilderIf = function (x, y, z, radius, builder, test) {
        var units = this.structure.units;
        var closeUnits = this.unitLookup.find(x, y, z, radius);
        if (closeUnits.count === 0)
            return;
        var loc = StructureElement.Location.create(this.structure);
        for (var t = 0, _t = closeUnits.count; t < _t; t++) {
            var unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            var unitLookup = unit.lookup3d;
            var groupResult = unitLookup.find(this.pivot[0], this.pivot[1], this.pivot[2], radius);
            if (groupResult.count === 0)
                continue;
            var elements = unit.elements;
            loc.unit = unit;
            builder.beginUnit(unit.id);
            for (var j = 0, _j = groupResult.count; j < _j; j++) {
                loc.element = elements[groupResult.indices[j]];
                if (test(loc)) {
                    builder.addElement(loc.element);
                }
            }
            builder.commitUnit();
        }
    };
    StructureLookup3D.prototype.findIntoBuilderWithRadius = function (x, y, z, pivotR, maxRadius, radius, eRadius, builder) {
        var units = this.structure.units;
        var closeUnits = this.unitLookup.find(x, y, z, radius);
        if (closeUnits.count === 0)
            return;
        var se = StructureElement.Location.create(this.structure);
        var queryRadius = pivotR + maxRadius + radius;
        for (var t = 0, _t = closeUnits.count; t < _t; t++) {
            var unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            var unitLookup = unit.lookup3d;
            var groupResult = unitLookup.find(this.pivot[0], this.pivot[1], this.pivot[2], queryRadius);
            if (groupResult.count === 0)
                continue;
            var elements = unit.elements;
            se.unit = unit;
            builder.beginUnit(unit.id);
            for (var j = 0, _j = groupResult.count; j < _j; j++) {
                se.element = elements[groupResult.indices[j]];
                var rr = eRadius(se);
                if (Math.sqrt(groupResult.squaredDistances[j]) - pivotR - rr > radius)
                    continue;
                builder.addElement(elements[groupResult.indices[j]]);
            }
            builder.commitUnit();
        }
    };
    StructureLookup3D.prototype.check = function (x, y, z, radius) {
        var units = this.structure.units;
        var closeUnits = this.unitLookup.find(x, y, z, radius);
        if (closeUnits.count === 0)
            return false;
        for (var t = 0, _t = closeUnits.count; t < _t; t++) {
            var unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            var groupLookup = unit.lookup3d;
            if (groupLookup.check(this.pivot[0], this.pivot[1], this.pivot[2], radius))
                return true;
        }
        return false;
    };
    Object.defineProperty(StructureLookup3D.prototype, "boundary", {
        get: function () {
            return this.structure.boundary;
        },
        enumerable: false,
        configurable: true
    });
    return StructureLookup3D;
}());
export { StructureLookup3D };
